import { useState } from 'react';
import { Mail, Phone, MapPin } from 'lucide-react';
import { useInView } from '../hooks/useInView';
import { contactInfo } from '../data/portfolioData';

const ICON_MAP = { email: Mail, phone: Phone, location: MapPin };

export default function Contact() {
  const [ref, inView] = useInView(0.1);
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sent, setSent] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Open mailto with prefilled content as fallback
    window.location.href =
      `mailto:guptarajshrestha@gmail.com?subject=${encodeURIComponent(form.subject)}&body=${encodeURIComponent(`Name: ${form.name}\nEmail: ${form.email}\n\n${form.message}`)}`;
    setSent(true);
  };

  return (
    <section id="contact" style={{ background: 'var(--bg2)' }} ref={ref}>
      <div className="max-w-5xl mx-auto px-6 lg:px-16 py-24">
        <div className={`reveal ${inView ? 'visible' : ''}`}>
          <div className="section-eyebrow">06 — Let's connect</div>
        </div>
        <h2 className={`reveal ${inView ? 'visible' : ''} delay-1`}
          style={{ fontSize: 'clamp(2rem,5vw,3.2rem)', fontWeight: 700, letterSpacing: '-0.02em', marginBottom: '0.75rem' }}>
          Get In Touch
        </h2>
        <p className={`reveal ${inView ? 'visible' : ''} delay-2`}
          style={{ color: 'var(--text2)', fontSize: '1.05rem', maxWidth: 520, lineHeight: 1.7, marginBottom: '3.5rem' }}>
          Open to new opportunities, collaborations, and interesting conversations.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14">
          {/* Contact info */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
            {contactInfo.map((item, i) => {
              const Icon = ICON_MAP[item.icon];
              return (
                <a
                  key={item.label}
                  href={item.href}
                  className={`reveal-left ${inView ? 'visible' : ''} delay-${i + 3}`}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '1rem',
                    padding: '1.15rem 1.4rem', borderRadius: 12,
                    border: '1px solid var(--border)', background: 'var(--card)',
                    textDecoration: 'none', color: 'inherit',
                    transition: 'all 0.3s', cursor: item.href === '#' ? 'default' : 'none',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(124,109,250,0.4)'; e.currentTarget.style.transform = 'translateX(4px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = ''; }}
                >
                  <div style={{
                    width: 44, height: 44, borderRadius: 10, flexShrink: 0,
                    background: 'rgba(124,109,250,0.1)', border: '1px solid rgba(124,109,250,0.2)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'var(--accent)',
                  }}>
                    <Icon size={20} />
                  </div>
                  <div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text2)', letterSpacing: '0.05em', marginBottom: 2 }}>{item.label}</div>
                    <div style={{ fontSize: '0.9rem', fontWeight: 500 }}>{item.value}</div>
                  </div>
                </a>
              );
            })}
          </div>

          {/* Form */}
          <div className={`reveal-right ${inView ? 'visible' : ''} delay-3`}>
            {sent ? (
              <div style={{
                padding: '2rem', borderRadius: 16, border: '1px solid rgba(109,250,213,0.3)',
                background: 'rgba(109,250,213,0.06)', textAlign: 'center',
              }}>
                <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>✅</div>
                <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Mail client opened!</div>
                <div style={{ fontSize: '0.88rem', color: 'var(--text2)' }}>Alternatively, reach out directly at guptarajshrestha@gmail.com</div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {[
                  { name: 'name',    label: 'Your Name',      type: 'text',  placeholder: 'John Doe' },
                  { name: 'email',   label: 'Email Address',  type: 'email', placeholder: 'john@example.com' },
                  { name: 'subject', label: 'Subject',         type: 'text',  placeholder: "What's this about?" },
                ].map(field => (
                  <div key={field.name}>
                    <label style={{ display: 'block', fontSize: '0.77rem', color: 'var(--text2)', marginBottom: '0.4rem', letterSpacing: '0.05em' }}>
                      {field.label}
                    </label>
                    <input
                      type={field.type}
                      placeholder={field.placeholder}
                      value={form[field.name]}
                      onChange={e => setForm({ ...form, [field.name]: e.target.value })}
                      required
                      style={{
                        width: '100%', padding: '0.8rem 1rem', borderRadius: 10,
                        border: '1px solid var(--border)', background: 'var(--card)',
                        color: 'var(--text)', fontSize: '0.9rem',
                        fontFamily: "'Space Grotesk', sans-serif",
                        outline: 'none', transition: 'border-color 0.3s, box-shadow 0.3s',
                        cursor: 'text',
                      }}
                      onFocus={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,109,250,0.15)'; }}
                      onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = ''; }}
                    />
                  </div>
                ))}
                <div>
                  <label style={{ display: 'block', fontSize: '0.77rem', color: 'var(--text2)', marginBottom: '0.4rem', letterSpacing: '0.05em' }}>
                    Message
                  </label>
                  <textarea
                    placeholder="Let's talk…"
                    rows={5}
                    value={form.message}
                    onChange={e => setForm({ ...form, message: e.target.value })}
                    required
                    style={{
                      width: '100%', padding: '0.8rem 1rem', borderRadius: 10,
                      border: '1px solid var(--border)', background: 'var(--card)',
                      color: 'var(--text)', fontSize: '0.9rem',
                      fontFamily: "'Space Grotesk', sans-serif",
                      outline: 'none', resize: 'vertical',
                      transition: 'border-color 0.3s, box-shadow 0.3s',
                      cursor: 'text',
                    }}
                    onFocus={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(124,109,250,0.15)'; }}
                    onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = ''; }}
                  />
                </div>
                <button
                  type="submit"
                  style={{
                    width: '100%', padding: '0.9rem',
                    borderRadius: '100px', fontSize: '0.9rem', fontWeight: 600,
                    background: 'linear-gradient(135deg, var(--accent), var(--accent2))',
                    color: 'white', border: 'none', cursor: 'none',
                    fontFamily: "'Space Grotesk', sans-serif",
                    boxShadow: '0 0 30px rgba(124,109,250,0.3)',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 0 50px rgba(124,109,250,0.5)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 0 30px rgba(124,109,250,0.3)'; }}
                >
                  Send Message
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
